import { LayoutDashboard, FileText, Users, Settings, Mic2, GraduationCap, Wine, Banknote, Radio, ClipboardList, X, ShieldCheck, Shield } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface SidebarProps {
  onClose?: () => void;
}

function Sidebar({ onClose }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const [pendingNeedsCount, setPendingNeedsCount] = useState(0);

  // Charger le nombre de besoins non traités (admin/pasteur uniquement)
  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'pasteur')) return;

    const fetchPendingNeeds = async () => {
      try {
        const q = query(
          collection(db, 'needs'),
          where('isAddressed', '==', false)
        );
        const snapshot = await getCountFromServer(q);
        setPendingNeedsCount(snapshot.data().count);
      } catch (err) {
        console.error('Erreur chargement besoins:', err);
      }
    };

    fetchPendingNeeds();
    // Rafraîchir toutes les 60 secondes
    const interval = setInterval(fetchPendingNeeds, 60_000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLinkClick = () => {
    if (onClose) onClose();
  };

  const hasAccessToReport = (reportId: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'pasteur') return true;
    if (user.role === 'responsable') {
      return user.assignedReports?.includes(reportId) || false;
    }
    return false;
  };

  const hasSettingsAccess = user?.role === 'admin' || user?.role === 'pasteur';
  const hasAnyReportAccess =
    hasAccessToReport('worshipReports') ||
    hasAccessToReport('adnReports') ||
    hasAccessToReport('financeReports') ||
    hasAccessToReport('academieReports') ||
    hasAccessToReport('sainteCeneReports') ||
    hasAccessToReport('sonoReports');

  const linkClass = (path: string) =>
    `flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-200 touch-manipulation ${
      location.pathname === path || location.pathname.startsWith(path + '/')
        ? 'bg-[#00665C]/10 text-[#00665C] font-medium'
        : 'text-gray-600 hover:bg-[#00665C]/5 hover:text-[#00665C] active:bg-[#00665C]/10'
    }`;

  return (
    <div className="w-full h-full bg-white border-r border-gray-200 overflow-y-auto flex flex-col">
      <div className="p-4 md:p-3 border-b border-gray-100 flex justify-between items-center">
        <img
          src="/images/LOGO_VH.png"
          alt="VH Logo"
          className="h-14 md:h-12 w-auto"
        />
        <button
          onClick={onClose}
          className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Fermer le menu"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 p-4 md:p-3 space-y-1">
        <h2 className="text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-2">
          GÉNÉRAL
        </h2>
        <Link to="/dashboard" onClick={handleLinkClick} className={linkClass('/dashboard')}>
          <LayoutDashboard size={18} className="flex-shrink-0" />
          <span className="text-sm md:text-xs">Tableau de bord</span>
        </Link>

        {hasAnyReportAccess && (
          <>
            <h2 className="text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mt-6 mb-2 px-2">
              RAPPORT DÉPARTEMENT
            </h2>
            {hasAccessToReport('worshipReports') && (
              <Link to="/worship-reports" onClick={handleLinkClick} className={linkClass('/worship-reports')}>
                <FileText size={18} className="flex-shrink-0" />
                <span className="text-sm md:text-xs">Gestion de Culte</span>
              </Link>
            )}
            {hasAccessToReport('adnReports') && (
              <Link to="/adn-reports" onClick={handleLinkClick} className={linkClass('/adn-reports')}>
                <Users size={18} className="flex-shrink-0" />
                <span className="text-sm md:text-xs">ADN</span>
              </Link>
            )}
            {hasAccessToReport('financeReports') && (
              <Link to="/finance-reports" onClick={handleLinkClick} className={linkClass('/finance-reports')}>
                <Banknote size={18} className="flex-shrink-0" />
                <span className="text-sm md:text-xs">Finance</span>
              </Link>
            )}
            {hasAccessToReport('academieReports') && (
              <Link to="/academie-reports" onClick={handleLinkClick} className={linkClass('/academie-reports')}>
                <GraduationCap size={18} className="flex-shrink-0" />
                <span className="text-sm md:text-xs">Académie</span>
              </Link>
            )}
            {hasAccessToReport('sainteCeneReports') && (
              <Link to="/sainte-cene-reports" onClick={handleLinkClick} className={linkClass('/sainte-cene-reports')}>
                <Wine size={18} className="flex-shrink-0" />
                <span className="text-sm md:text-xs">Sainte Cène</span>
              </Link>
            )}
            {hasAccessToReport('sonoReports') && (
              <Link to="/sono-reports" onClick={handleLinkClick} className={linkClass('/sono-reports')}>
                <Radio size={18} className="flex-shrink-0" />
                <span className="text-sm md:text-xs">Communication & Sono</span>
              </Link>
            )}
          </>
        )}

        <hr className="my-3 border-gray-200" />
        <h2 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Gestion</h2>

        {hasSettingsAccess && (
          <>
            <h2 className="text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider mt-6 mb-2 px-2">
              PARAMÈTRES
            </h2>
            <Link to="/users" onClick={handleLinkClick} className={linkClass('/users')}>
              <Users size={18} className="flex-shrink-0" />
              <span className="text-sm md:text-xs">Utilisateurs</span>
            </Link>
            <Link to="/roles" onClick={handleLinkClick} className={linkClass('/roles')}>
              <ShieldCheck size={18} className="flex-shrink-0" />
              <span className="text-sm md:text-xs">Rôles & Permissions</span>
            </Link>
            <Link to="/settings/meeting-types" onClick={handleLinkClick} className={linkClass('/settings/meeting-types')}>
              <Settings size={18} className="flex-shrink-0" />
              <span className="text-sm md:text-xs">Types de rencontre</span>
            </Link>
            <Link to="/settings/speakers" onClick={handleLinkClick} className={linkClass('/settings/speakers')}>
              <Mic2 size={18} className="flex-shrink-0" />
              <span className="text-sm md:text-xs">Orateurs</span>
            </Link>

            {/* Lien Besoins avec badge */}
            <Link
              to="/needs-management"
              onClick={handleLinkClick}
              className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-200 touch-manipulation ${
                location.pathname === '/needs-management'
                  ? 'bg-[#00665C]/10 text-[#00665C] font-medium'
                  : 'text-gray-600 hover:bg-[#00665C]/5 hover:text-[#00665C] active:bg-[#00665C]/10'
              }`}
            >
              <div className="flex items-center space-x-2">
                <ClipboardList size={18} className="flex-shrink-0" />
                <span className="text-sm md:text-xs">Gestion des besoins</span>
              </div>
              {pendingNeedsCount > 0 && (
                <span
                  className="flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center leading-none"
                  title={`${pendingNeedsCount} besoin${pendingNeedsCount > 1 ? 's' : ''} en attente`}
                >
                  {pendingNeedsCount > 99 ? '99+' : pendingNeedsCount}
                </span>
              )}
            </Link>

            {user?.role === 'admin' && (
              <Link to="/login-logs" onClick={handleLinkClick} className={linkClass('/login-logs')}>
                <Shield size={18} className="flex-shrink-0" />
                <span className="text-sm md:text-xs">Traçabilité</span>
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Sidebar;
